/**
 * Ticket 2695 (Martin: „Ich möchte das System unterbrechen können“) — Weg A: EIN Tipp auf die laufende Antwort
 * (die Sprech-Pille) stoppt das Vorlesen UND öffnet sofort das Mikro.
 *
 * Kein zweiter Aufnahmeweg: `starte` ist der vorhandene Freihand-Start der Sprechtaste (startPttHandsFree →
 * startPttRecording). Beides läuft im selben Klick — dieselbe Geste, in der iOS Ton und Mikro erlaubt.
 *
 * Nur wenn wirklich etwas klingt (Sprechen/Pausiert) UND eine Aufnahme starten darf (kein Anruf, keine laufende
 * Aufnahme). Sonst bleibt es beim alten Verhalten der Pille: nur stoppen.
 */
export type PilleZustand = "prep" | "playing" | "paused" | "wartet" | "hidden";

/** Tipp („pille“) oder Tastatur („pille-taste“) — bleibt im Stoppgrund und in der Messzeile unterscheidbar. */
export type PilleKanal = "pille" | "pille-taste";

export interface UnterbrechenAbhaengig {
  kanal: PilleKanal;
  zustand: PilleZustand;
  /** false im Anruf-Modus, bei laufender/sendender Aufnahme — dann nur stoppen. */
  aufnahmeMoeglich: boolean;
  stoppe: (grund: string) => void;
  /** `ttsLief`: vor dem Stopp ermittelt (die Fortsetzungs-Messung braucht, was VOR dem Stopp klang). */
  starte: (ttsLief: boolean) => void;
  /** Klang die Wiedergabe jetzt? Wird vor dem Stopp gelesen. */
  ttsLief: () => boolean;
  jetzt: () => number;
}

export type UnterbrechenErgebnis = "unterbrochen" | "nur-stopp";

let t0: number | null = null;
let kanal: PilleKanal = "pille";

export function unterbrechePille(o: UnterbrechenAbhaengig): UnterbrechenErgebnis {
  const klingt = o.zustand === "playing" || o.zustand === "paused";
  if (!klingt || !o.aufnahmeMoeglich) {
    o.stoppe(o.kanal);
    return "nur-stopp";
  }
  t0 = o.jetzt();
  kanal = o.kanal;
  const lief = o.ttsLief();
  o.stoppe(o.kanal);
  o.starte(lief);
  return "unterbrochen";
}

/** Das Mikro ist offen: die Messzeile (nur Zahlen) — genau einmal je Unterbrechung, sonst null. */
export function unterbrechenMikroOffen(jetzt: number): string | null {
  if (t0 === null) return null;
  const dauer = Math.max(0, Math.round(jetzt - t0));
  t0 = null;
  return `[unterbrechen] via=${kanal} dauerBisMikroMs=${dauer}`;
}

/**
 * Darf der Tipp auch das Mikro öffnen? Die Pille steht in JEDER Ansicht; die Aufnahme springt aber in den Chat und
 * schließt dabei, was darüber liegt (Kontakt-Formular, Rechnung, Mein Alltag) — ungespeicherte Eingaben gingen
 * verloren. Darum nur im Chat ohne offene Seite; sonst bleibt es beim Stoppen.
 */
export function darfMikroOeffnen(a: {
  anruf: boolean;
  pttIdle: boolean;
  sperre: boolean;
  /** body.dataset.activeView; leer = noch keine Ansicht gesetzt. */
  ansicht: string | undefined;
  etwasUeberDemChat: boolean;
}): boolean {
  if (a.anruf || !a.pttIdle || a.sperre) return false;
  if (a.ansicht && a.ansicht !== "chat") return false;
  return !a.etwasUeberDemChat;
}

/** Der Start kam nie zur Aufnahme (Mikro verweigert, abgewiesen): keine Messung stehen lassen. */
export function unterbrechenVerwerfen(): void {
  t0 = null;
}
